// Utility function for filtering lab types based on localStorage labTypeIds

// Standard lab types definition
export const LAB_TYPES = [
  { id: 1, name: 'Testing' },
  { id: 2, name: 'Calibration' },
  { id: 3, name: 'Medical' }
];

/**
 * Get allowed lab types from localStorage labTypeIds
 * @param {Array} labTypes - Array of lab type objects (optional, defaults to LAB_TYPES)
 * @returns {Array} Filtered array of allowed lab types
 */
export const getAllowedLabTypes = (labTypes = LAB_TYPES) => {
  const storedLabTypeIds = localStorage.getItem('labTypeIds');
  if (!storedLabTypeIds) {
    console.log('No labTypeIds in localStorage, showing all lab types');
    return labTypes; // Return all if no restriction
  }
  
  console.log('Raw labTypeIds from localStorage:', storedLabTypeIds);
  
  // Parse comma-separated labTypeIds
  const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
  
  console.log('Parsed allowed lab type IDs:', allowedIds);
  
  // Filter labTypes to only include allowed ones
  const filtered = labTypes.filter(labType => allowedIds.includes(labType.id));
  
  console.log('Filtered lab types:', filtered);
  
  return filtered;
};

/**
 * Get lab type name by ID
 * @param {number|string} labTypeId - The lab type ID
 * @param {Array} labTypes - Array of lab type objects (optional, defaults to LAB_TYPES)
 * @returns {string} Lab type name or 'Unknown'
 */
export const getLabTypeName = (labTypeId, labTypes = LAB_TYPES) => {
  const labType = labTypes.find(type => type.id === Number(labTypeId));
  return labType ? labType.name : 'Unknown';
};

/**
 * Get initial lab type ID from localStorage
 * Prioritizes labTypeIds, falls back to labTypeId
 * If only one lab type is allowed, returns that ID
 * @returns {number|null} Lab type ID or null
 */
export const getInitialLabTypeId = () => {
  const storedLabTypeIds = localStorage.getItem('labTypeIds');
  const storedLabTypeId = localStorage.getItem('labTypeId');
  
  if (storedLabTypeIds) {
    // Parse comma-separated labTypeIds
    const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    console.log(`Allowed Lab Type IDs from localStorage: ${allowedIds.join(', ')}`);
    
    // If only one lab type is allowed, return it
    if (allowedIds.length === 1) {
      const labTypeId = allowedIds[0];
      const labTypeName = getLabTypeName(labTypeId);
      console.log(`Single Lab Type ID: ${labTypeId}, Lab Type: ${labTypeName}`);
      return labTypeId;
    }
    
    return null; // Multiple options available
  } else if (storedLabTypeId) {
    // Fallback to old single labTypeId
    const labTypeId = Number(storedLabTypeId);
    const labTypeName = getLabTypeName(labTypeId);
    console.log(`Fallback Lab Type ID: ${labTypeId}, Lab Type: ${labTypeName}`);
    return labTypeId;
  }
  
  return null;
};

/**
 * Get first lab type ID from localStorage for filtering/default selection
 * Always returns the first available lab type regardless of count
 * @returns {number|null} First lab type ID or null
 */
export const getFirstLabTypeId = () => {
  const storedLabTypeIds = localStorage.getItem('labTypeIds');
  const storedLabTypeId = localStorage.getItem('labTypeId');
  
  if (storedLabTypeIds) {
    // Parse comma-separated labTypeIds and return first one
    const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    console.log(`Allowed Lab Type IDs from localStorage: ${allowedIds.join(', ')}`);
    
    if (allowedIds.length > 0) {
      const firstLabTypeId = allowedIds[0];
      const labTypeName = getLabTypeName(firstLabTypeId);
      console.log(`First Lab Type ID: ${firstLabTypeId}, Lab Type: ${labTypeName}`);
      return firstLabTypeId;
    }
  } else if (storedLabTypeId) {
    // Fallback to old single labTypeId
    const labTypeId = Number(storedLabTypeId);
    const labTypeName = getLabTypeName(labTypeId);
    console.log(`Fallback Lab Type ID: ${labTypeId}, Lab Type: ${labTypeName}`);
    return labTypeId;
  }
  
  return null;
};

/**
 * Check if dropdown should be disabled
 * Disables if only one or no lab types are available
 * @param {Array} filteredLabTypes - Array of filtered lab types
 * @returns {boolean} True if should be disabled
 */
export const shouldDisableDropdown = (filteredLabTypes) => {
  return filteredLabTypes.length <= 1;
};